import { memo, useMemo, type HTMLAttributes, type MouseEvent, type ReactNode } from "react";
import { Marked } from "marked";
import { Parser } from "htmlparser2";
import { useNavigate } from "react-router-dom";
import { cn } from "@/components/ui/utils";

const ALLOWED_TAGS = new Set([
  "p",
  "br",
  "hr",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "ul",
  "ol",
  "li",
  "pre",
  "code",
  "strong",
  "em",
  "del",
  "blockquote",
  "a",
  "table",
  "thead",
  "tbody",
  "tr",
  "th",
  "td",
]);
const VOID_TAGS = new Set(["br", "hr"]);
const SKIP_CONTENTS = new Set([
  "script",
  "style",
  "svg",
  "math",
  "iframe",
  "object",
  "embed",
  "link",
  "meta",
  "base",
  "form",
  "template",
  "noscript",
  "head",
  "textarea",
  "foreignobject",
  "frame",
  "frameset",
  "xmp",
  "noembed",
  "noframes",
  "plaintext",
]);
const ALLOWED_ATTRS: Record<string, ReadonlySet<string>> = {
  a: new Set(["href", "title"]),
  code: new Set(["class"]),
  ol: new Set(["start"]),
  th: new Set(["align"]),
  td: new Set(["align"]),
};
// Assistant replies are model-written, so nothing in them may make the browser
// fetch on render: no images, and links only to https, mailto, or an in-app path.
const EXTERNAL_URL_SCHEMES = new Set(["https:", "mailto:"]);
const URL_SCHEME = /^([a-zA-Z][a-zA-Z0-9+.-]*):/;

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function isUrlNoise(code: number): boolean {
  return (
    code <= 0x1f ||
    (code >= 0x7f && code <= 0x9f) ||
    (code >= 0x200b && code <= 0x200f) ||
    (code >= 0x2028 && code <= 0x202f) ||
    (code >= 0x2060 && code <= 0x206f) ||
    code === 0xfeff
  );
}

function sanitizeUrl(raw: string): string | null {
  let stripped = "";
  for (const char of raw) {
    if (isUrlNoise(char.charCodeAt(0)) || char.trim() === "") continue;
    stripped += char;
  }
  if (!stripped) return null;
  if (isInAppPath(stripped)) return stripped;
  const scheme = URL_SCHEME.exec(stripped)?.[0]?.toLowerCase();
  return scheme && EXTERNAL_URL_SCHEMES.has(scheme) ? stripped : null;
}

// "//host" and "/\host" are protocol-relative to browsers, so they are external.
function isInAppPath(url: string): boolean {
  return url.startsWith("/") && url[1] !== "/" && url[1] !== "\\";
}

function externalHost(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return "";
  }
}

function sanitizeAttribute(tag: string, name: string, value: string): string | null {
  if (name.startsWith("on") || name === "style" || name === "srcdoc") return null;
  if (!ALLOWED_ATTRS[tag]?.has(name)) return null;
  if (name === "href") return sanitizeUrl(value);
  if (name === "class") {
    const trimmed = value.trim();
    return /^language-[a-z0-9_+-]+$/i.test(trimmed) ? trimmed : null;
  }
  if (name === "align") {
    const trimmed = value.trim().toLowerCase();
    return trimmed === "left" || trimmed === "center" || trimmed === "right" ? trimmed : null;
  }
  if (name === "start") {
    const trimmed = value.trim();
    return /^\d{1,9}$/.test(trimmed) ? trimmed : null;
  }
  return value;
}

/** The extra attributes a link needs, or null when its href is missing or unsafe. */
function linkExtras(href: string | undefined): string | null {
  const safe = href === undefined ? null : sanitizeUrl(href);
  if (safe === null) return null;
  return isInAppPath(safe) ? "" : ' target="_blank" rel="noopener noreferrer"';
}

/** An allowed tag rebuilt from its whitelisted attributes; null drops the tag (an `a` needs a safe href). */
function openingTag(name: string, attributes: Record<string, string>): string | null {
  const extras = name === "a" ? linkExtras(attributes.href) : "";
  if (extras === null) return null;
  const attrs = Object.entries(attributes).flatMap(([attrName, attrValue]) => {
    const sanitized = sanitizeAttribute(name, attrName, attrValue);
    return sanitized === null ? [] : [` ${attrName}="${escapeHtml(sanitized)}"`];
  });
  return `<${name}${attrs.join("")}${extras}>`;
}

// marked emits raw HTML tokens; rebuild from the whitelist before innerHTML.
function sanitizeHtml(html: string): string {
  const out: string[] = [];
  const open: string[] = [];
  let skipDepth = 0;
  const parser = new Parser(
    {
      onopentag(name, attributes) {
        if (skipDepth > 0) {
          skipDepth++;
          return;
        }
        if (SKIP_CONTENTS.has(name)) {
          skipDepth = 1;
          return;
        }
        const tag = ALLOWED_TAGS.has(name) ? openingTag(name, attributes) : null;
        if (tag === null) return;
        out.push(tag);
        if (!VOID_TAGS.has(name)) open.push(name);
      },
      ontext(text) {
        if (skipDepth > 0) return;
        out.push(escapeHtml(text));
      },
      onclosetag(name) {
        if (skipDepth > 0) {
          skipDepth--;
          return;
        }
        if (VOID_TAGS.has(name) || open.at(-1) !== name) return;
        open.pop();
        out.push(`</${name}>`);
      },
    },
    { decodeEntities: true, lowerCaseTags: true, lowerCaseAttributeNames: true },
  );
  parser.end(html);
  while (open.length > 0) {
    const name = open.pop();
    if (name) out.push(`</${name}>`);
  }
  return out.join("");
}

const markdownParser = new Marked({
  async: false,
  gfm: true,
  breaks: true,
  renderer: {
    html({ text }) {
      return escapeHtml(text);
    },
    link({ href, title, text: rawText, tokens }) {
      const text = this.parser.parseInline(tokens);
      const safeHref = sanitizeUrl(href);
      if (!safeHref) return text;
      const titleAttr = title ? ` title="${escapeHtml(title)}"` : "";
      if (isInAppPath(safeHref)) return `<a href="${escapeHtml(safeHref)}"${titleAttr}>${text}</a>`;
      // Show the real destination so link text cannot disguise it.
      const host = externalHost(safeHref);
      const hostLabel = host && rawText !== href ? ` (${escapeHtml(host)})` : "";
      return `<a href="${escapeHtml(safeHref)}"${titleAttr} target="_blank" rel="noopener noreferrer">${text}${hostLabel}</a>`;
    },
    image({ text }) {
      return escapeHtml(text);
    },
  },
});

export interface ResponseProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
  className?: string;
}

export const Response = memo(
  ({ children, className, ...props }: ResponseProps) => {
    const navigate = useNavigate();
    const isStringChild = typeof children === "string";

    const parsedHtml = useMemo(() => {
      if (!isStringChild) return null;
      try {
        return sanitizeHtml(markdownParser.parse(children) as string);
      } catch {
        return null;
      }
    }, [children, isStringChild]);

    if (isStringChild && parsedHtml !== null) {
      // The HTML is injected, so in-app links are routed here instead of
      // through <Link>; modified clicks keep the browser's new-tab behavior.
      const routeInAppLink = (event: MouseEvent<HTMLDivElement>) => {
        props.onClick?.(event);
        if (event.defaultPrevented || event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const anchor = event.target instanceof Element ? event.target.closest("a") : null;
        const href = anchor?.getAttribute("href");
        if (!href || !isInAppPath(href)) return;
        event.preventDefault();
        void navigate(href);
      };

      return (
        // oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- delegates clicks from the injected anchors, which stay focusable and fire click on Enter
        <div
          className={cn(
            "size-full min-w-0 overflow-x-auto text-body text-foreground leading-relaxed",
            "[&>*:first-child]:mt-0 [&>*:last-child]:mb-0",
            "[&_p]:my-1.5 [&_h1]:my-3 [&_h2]:my-2.5 [&_h3]:my-2 [&_h4]:my-2 [&_h5]:my-1.5 [&_h6]:my-1.5",
            "[&_h1]:font-display [&_h1]:text-h4 [&_h2]:font-display [&_h2]:text-h5 [&_h3]:font-display [&_h3]:text-h6",
            "[&_h4]:font-display [&_h4]:text-body-semibold [&_h5]:font-display [&_h5]:text-body-semibold [&_h6]:font-display [&_h6]:text-meta",
            "[&_ul]:my-1.5 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-1.5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5 [&_li]:pl-0.5",
            "[&_pre]:my-2 [&_pre]:p-2.5 [&_pre]:rounded-md [&_pre]:bg-muted/80",
            "[&_code]:text-xs [&_code]:font-mono [&_code]:bg-muted/60 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded-md [&_pre_code]:bg-transparent [&_pre_code]:px-0 [&_pre_code]:py-0 [&_pre_code]:rounded-none",
            "[&_table]:my-2 [&_table]:w-full [&_table]:border-collapse [&_table]:text-sm",
            "[&_th]:border [&_th]:border-border [&_th]:bg-muted [&_th]:px-2.5 [&_th]:py-2 [&_th]:text-left [&_th]:font-medium [&_th]:whitespace-nowrap",
            "[&_td]:border [&_td]:border-border [&_td]:px-2.5 [&_td]:py-2 [&_td]:align-top",
            "[&_th[align=right]]:text-right [&_td[align=right]]:text-right [&_th[align=center]]:text-center [&_td[align=center]]:text-center",
            "[&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground",
            "[&_hr]:my-3 [&_hr]:border-border [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2",
            className,
          )}
          {...props}
          onClick={routeInAppLink}
          dangerouslySetInnerHTML={{ __html: parsedHtml }}
        />
      );
    }

    return (
      <div
        className={cn(
          "size-full leading-relaxed [&>*:first-child]:mt-0 [&>*:last-child]:mb-0",
          className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  },
  (prevProps, nextProps) => prevProps.children === nextProps.children,
);

Response.displayName = "Response";
